// EnvironmentInspector — read-only metadata + editable default_pose for a
// gsplat environment_assets row. ENG-529.
//
// Pose fields mirror liveTuneStore.environment.pose: px/py/pz/rx/ry/rz/scale.
// Optional floorY override matches splatFloorHeight.js. Save is explicit
// (form-style commit on blur or Enter) — the inspector is the editing
// surface, the runtime scene picks up the new defaults next time the env
// is selected in LiveTunePanel.

import React, { useEffect, useRef, useState } from 'react'
import { CustomAssetManager } from '../../lib/CustomAssetManager'
import { applyEnvironment } from '../../lib/applyEnvironment'
import { isRecordGoneError, STALE_ASSET_MESSAGE } from '../../lib/assetErrors'
import { liveTuneStore } from '../../lib/liveTuneStore'
import { normalizePose } from '../../lib/envPose'
import { downloadEnvironmentExport } from '../../lib/export/environmentExport'
import AnchorsSection from './AnchorsSection'
import PartsSection from './PartsSection'

const C = {
  bg:      '#0c0e15',
  border:  '#1f2129',
  text:    '#e1e1ed',
  dim:     '#8f8fa2',
  primary: '#bec2ff',
  danger:  '#f87171',
  ok:      '#4caf82',
}

const POSE_FIELDS = [
  { key: 'px',    label: 'Position X', step: 0.01 },
  { key: 'py',    label: 'Position Y', step: 0.01 },
  { key: 'pz',    label: 'Position Z', step: 0.01 },
  { key: 'rx',    label: 'Rotation X (rad)', step: 0.01 },
  { key: 'ry',    label: 'Rotation Y (rad)', step: 0.01 },
  { key: 'rz',    label: 'Rotation Z (rad)', step: 0.01 },
  { key: 'scale', label: 'Scale',      step: 0.01 },
  { key: 'floorY', label: 'Floor Y override (blank = auto)', step: 0.01, optional: true },
]

function readPose(asset) {
  const raw = asset.defaultPose ?? asset.raw?.default_pose ?? asset.raw?.defaultPose ?? {}
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) } catch { return {} }
  }
  return raw ?? {}
}

export default function EnvironmentInspector({ asset }) {
  // normalizePose gives 3D-software defaults: position 0, rotation 0, scale 1,
  // floor auto — so a fresh env shows Scale 1 (not blank/0) and never displays
  // an invisible 0 scale.
  const [pose, setPose] = useState(() => normalizePose(readPose(asset)))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const lastSavedRef = useRef(JSON.stringify(normalizePose(readPose(asset))))

  useEffect(() => {
    const next = normalizePose(readPose(asset))
    setPose(next)
    lastSavedRef.current = JSON.stringify(next)
    setError(null)
  }, [asset.id])

  const commit = async () => {
    const serialized = JSON.stringify(pose)
    if (serialized === lastSavedRef.current) return
    setSaving(true); setError(null)
    try {
      // camelCase `defaultPose` — the asset-manager's Update handler binds
      // json:"defaultPose"; a snake_case key is silently ignored (200, no save).
      await CustomAssetManager.updateEnvironmentAsset(asset.rawId, { defaultPose: pose })
      lastSavedRef.current = serialized
    } catch (e) {
      // updateEnvironmentAsset already re-synced the list on a stale id; show a
      // clear message instead of the raw "record not found" server string.
      setError(isRecordGoneError(e) ? STALE_ASSET_MESSAGE : String(e?.message ?? e))
    } finally {
      setSaving(false)
    }
  }

  // Dangling-safe apply (ported from the removed OrgEnvironmentsSection): if
  // another session deleted this env from the org bucket while the inspector
  // was open, refuse to apply a dead URL and say so instead.
  const apply = () => {
    const bucket = CustomAssetManager.listEnvironmentAssets()
    if (!bucket.some(e => e.id === asset.rawId)) {
      setError('Environment no longer exists in the org bucket')
      return
    }
    setError(null)
    applyEnvironment(asset)
  }

  // Bake trigger (ported from the removed OrgEnvironmentsSection). Fires the
  // org bake endpoint for THIS asset — works on any env, not just the active
  // one (the viewport LightingStatusChip only reaches the applied env).
  const doBake = async () => {
    setError(null)
    try {
      await CustomAssetManager.bakeEnvironmentAsset(asset.rawId, {})
    } catch (e) {
      const msg = String(e?.message ?? e)
      // 403 → non-admin; keep the friendly message the old section showed.
      setError(e?.status === 403 || msg.includes('403')
        ? 'You do not have permission to bake this environment (org-admin only)'
        : msg)
    }
  }

  // Live-preview pose edits: if this asset is the currently-applied
  // environment, push each edit straight into the live scene so the splat
  // moves/rotates/scales as you type — no "Apply" or reload needed. Editing a
  // non-active asset only stages to local state + DB (it shouldn't perturb the
  // scene showing a different env).
  const assetUrl = asset.url ?? asset.fileUrl ?? asset.file_url ?? asset.raw?.fileUrl ?? asset.raw?.file_url
  const liveUpdate = (nextPose) => {
    if (liveTuneStore.getState().environment?.splatUrl === assetUrl) {
      liveTuneStore.setState({ environment: { pose: normalizePose(nextPose) } })
    }
  }

  // Bake state + provenance (mapped in assetRegistry; fall back to the raw row).
  const raw = asset.raw ?? {}
  const bakeStatus = asset.bakeStatus ?? raw.bakeStatus ?? raw.bake_status ?? 'none'
  const bakedAt = asset.bakedAt ?? raw.bakedAt ?? raw.baked_at ?? null
  const bakedBy = asset.bakedBy ?? raw.bakedBy ?? raw.baked_by ?? null

  const [exporting, setExporting] = useState(false)
  const [exportMsg, setExportMsg] = useState(null)
  const [exportErr, setExportErr] = useState(null)
  const [exportProgress, setExportProgress] = useState(null)
  const lastPctRef = useRef(-1)

  const onExportProgress = ({ phase, loaded, total }) => {
    const mb = (n) => (n / 1048576).toFixed(1)
    let label, pct
    if (phase === 'downloading') {
      pct = total > 0 ? Math.round((loaded / total) * 100) : null
      // Throttle re-renders to whole-percent changes (chunks fire often).
      if (pct !== null && pct === lastPctRef.current) return
      lastPctRef.current = pct ?? -1
      label = total > 0
        ? `Downloading splat… ${pct}% (${mb(loaded)}/${mb(total)} MB)`
        : `Downloading splat… ${mb(loaded)} MB`
    } else {
      lastPctRef.current = -1
      pct = null
      label = {
        lighting: 'Fetching lighting…',
        packaging: 'Packaging…',
        compressing: 'Compressing… (large files take a moment)',
        done: 'Finalizing…',
      }[phase] ?? 'Working…'
    }
    setExportProgress({ label, pct })
  }

  const doExport = async () => {
    setExporting(true); setExportErr(null); setExportMsg(null)
    setExportProgress({ label: 'Starting…', pct: null }); lastPctRef.current = -1
    try {
      const { fileName, hasIbl, hasLightprobes, hasReflections, packageName } =
        await downloadEnvironmentExport(asset, { onProgress: onExportProgress })
      const lighting = [
        hasIbl && 'equirect',
        hasLightprobes && 'probes',
        hasReflections && 'reflections',
      ].filter(Boolean)
      const lightingNote = lighting.length
        ? `Lighting: ${lighting.join(', ')}.`
        : 'No baked lighting — exported unlit.'
      setExportMsg(
        `Downloaded ${fileName}. ${lightingNote}\n` +
        `Assets-only package. Hand to an agent:\n  npm install ${packageName}\n` +
        `  import { manifest, splatUrl, iblUrl } from '${packageName}'`
      )
    } catch (e) {
      setExportErr(String(e?.message ?? e))
    } finally {
      setExporting(false)
      setExportProgress(null)
    }
  }

  return (
    <div style={{ padding: '4px 12px 12px', borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim, padding: '8px 0 4px', letterSpacing: '.08em' }}>
        ENVIRONMENT
      </div>

      <button
        onClick={apply}
        style={{
          width: '100%', textAlign: 'center',
          background: '#0f2236', color: '#5cd4ff',
          border: `1px solid #5cd4ff55`,
          borderRadius: 3, padding: '6px 8px',
          fontFamily: 'var(--m-font)', fontSize: 11,
          cursor: 'pointer', marginBottom: 8,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#143049' }}
        onMouseLeave={e => { e.currentTarget.style.background = '#0f2236' }}
        title="Set as active env (auto-switches to Live View)"
      >
        🌅  Apply environment
      </button>

      <button
        onClick={doExport}
        disabled={exporting}
        data-testid="export-environment-package"
        style={{
          width: '100%', textAlign: 'center',
          background: '#10241a', color: C.ok,
          border: `1px solid ${C.ok}55`,
          borderRadius: 3, padding: '6px 8px',
          fontFamily: 'var(--m-font)', fontSize: 11,
          cursor: exporting ? 'wait' : 'pointer', marginBottom: 8,
          opacity: exporting ? 0.6 : 1,
        }}
        title="Export this environment as a self-contained npm package (.tgz)"
      >
        {exporting ? '⏳  Building package…' : '📦  Export npm package'}
      </button>
      {exportProgress && (
        <div data-testid="export-progress" style={{ marginBottom: 8 }}>
          <div style={{ fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim, marginBottom: 3 }}>
            {exportProgress.label}
          </div>
          <div style={{ height: 6, background: '#0a140f', border: `1px solid ${C.ok}33`, borderRadius: 3, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                background: C.ok,
                // Determinate when we have a %, else an indeterminate fill.
                width: exportProgress.pct != null ? `${exportProgress.pct}%` : '100%',
                opacity: exportProgress.pct != null ? 1 : 0.4,
                transition: 'width 0.15s linear',
              }}
            />
          </div>
        </div>
      )}
      {exportMsg && (
        <pre data-testid="export-success" style={{
          whiteSpace: 'pre-wrap', fontFamily: 'var(--m-mono)', fontSize: 10,
          color: C.ok, background: '#0a140f', border: `1px solid ${C.ok}33`,
          borderRadius: 3, padding: 6, marginBottom: 8,
        }}>{exportMsg}</pre>
      )}
      {exportErr && (
        <div data-testid="export-error" style={{
          fontFamily: 'var(--m-font)', fontSize: 10, color: C.danger,
          background: '#1a0d0d', border: `1px solid ${C.danger}33`,
          borderRadius: 3, padding: 6, marginBottom: 8,
        }}>Export failed: {exportErr}</div>
      )}

      <Row label="Format"><Mono>{asset.format ?? '—'}</Mono></Row>
      <Row label="Size"><Mono>{fmtSize(asset.byteSize)}</Mono></Row>
      <Row label="File URL"><Mono ellipsis>{asset.url ?? '—'}</Mono></Row>

      <div style={{ fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim, padding: '12px 0 4px', letterSpacing: '.08em' }}>
        LIGHTING
      </div>
      <Row label="Status"><BakeStatusText status={bakeStatus} /></Row>
      <Row label="Last baked"><Mono>{fmtDate(bakedAt)}</Mono></Row>
      <Row label="Baked by"><Mono>{bakedBy ?? '—'}</Mono></Row>
      <button
        onClick={doBake}
        disabled={bakeStatus === 'baking'}
        data-testid="env-bake-button"
        style={{
          width: '100%', textAlign: 'center',
          background: C.bg, color: C.primary,
          border: `1px solid ${C.primary}55`,
          borderRadius: 3, padding: '6px 8px',
          fontFamily: 'var(--m-font)', fontSize: 11,
          cursor: bakeStatus === 'baking' ? 'not-allowed' : 'pointer',
          opacity: bakeStatus === 'baking' ? 0.6 : 1,
          marginTop: 6,
        }}
        title={bakeStatus === 'baking' ? 'Already baking' : 'Bake lighting (IBL + probes + reflections) for this environment'}
      >
        {bakeStatus === 'baking' ? '⏳  Baking…' : '☀  Bake lighting'}
      </button>

      <div style={{ fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim, padding: '12px 0 4px', letterSpacing: '.08em' }}>
        DEFAULT POSE
      </div>

      {POSE_FIELDS.map(f => (
        <PoseField
          key={f.key}
          label={f.label}
          step={f.step}
          optional={f.optional}
          value={pose[f.key]}
          onChange={v => setPose(p => { const next = { ...p, [f.key]: v }; liveUpdate(next); return next })}
          onCommit={commit}
        />
      ))}

      {saving && <div style={{ fontSize: 10, color: C.dim, marginTop: 6 }}>Saving…</div>}
      {error  && <div style={{ fontSize: 10, color: C.danger, marginTop: 6 }}>{error}</div>}

      {/* ENG-590. Its own save + DirtyBadge: anchors live on a different column
          than the pose, are authored from the viewport as well as here, and the
          pose fields commit on blur — one shared Save would make a blur commit
          anchors the user had not finished editing. */}
      {/* ENG-1132. Above ANCHORS deliberately: a part is a thing in the room and
          an anchor is a place you can send someone, and the common flow is to
          add the sofa and then make an anchor from it — which is the button on
          the part's own row. */}
      <PartsSection assetId={asset.rawId} assetName={asset.name} C={C} />
      <AnchorsSection assetId={asset.rawId} assetName={asset.name} C={C} />
    </div>
  )
}

function PoseField({ label, value, step, optional, onChange, onCommit }) {
  const display = value == null || value === '' ? '' : String(value)
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
      <span style={{ flex: '0 0 200px', fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim }}>{label}</span>
      <input
        type="number"
        step={step ?? 0.01}
        value={display}
        placeholder={optional ? 'auto' : '0'}
        onChange={e => {
          const raw = e.target.value
          if (raw === '') onChange(optional ? null : 0)
          else onChange(Number(raw))
        }}
        onBlur={onCommit}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
        style={{
          flex: 1, background: '#080a10', border: `1px solid ${C.border}`,
          color: C.text, fontFamily: 'var(--m-font)', fontSize: 11,
          padding: '3px 6px', borderRadius: 3,
        }}
      />
    </label>
  )
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
      <span style={{ flex: '0 0 90px', fontFamily: 'var(--m-font)', fontSize: 10, color: C.dim }}>{label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  )
}

function Mono({ children, ellipsis }) {
  return (
    <span style={{
      fontFamily: 'var(--m-mono)', fontSize: 11, color: C.text,
      ...(ellipsis ? { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
    }}>
      {children}
    </span>
  )
}

const BAKE_LABEL = {
  ready:  { fg: '#4caf82', text: 'Baked' },
  baking: { fg: '#a78bfa', text: 'Baking…' },
  error:  { fg: '#f87171', text: 'Bake failed' },
  none:   { fg: '#8f8fa2', text: 'Not baked' },
}
function BakeStatusText({ status }) {
  const s = BAKE_LABEL[status] ?? BAKE_LABEL.none
  return <span style={{ fontFamily: 'var(--m-font)', fontSize: 11, color: s.fg }}>● {s.text}</span>
}

function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d)) return '—'
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function fmtSize(bytes) {
  if (bytes == null || bytes === 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = Number(bytes), i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`
}
